import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useNovel } from '@/context/novel-context';
import { useNarrativeItem } from '@/hooks/use-entity';
import type { EventSummary, Scene } from '@/lib/types';

import { SceneFormSchema, type SceneFormValues } from '../schemas';
import { useDeleteScene, useUpdateScene } from './use-mutation-hooks';
import { useSceneEventAutoFill } from './use-scene-event-auto-fill';

interface UseEditSceneFormProps {
  sceneId: string;
}

export function useEditSceneForm({ sceneId }: UseEditSceneFormProps) {
  const { novel } = useNovel();
  const { t } = useTranslation();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedEventForPopup, setSelectedEventForPopup] = useState<EventSummary | null>(null);

  const { data: sceneData, isLoading: isLoadingScene } = useNarrativeItem<Scene>('scenes', sceneId);
  const { updateScene, isSaving } = useUpdateScene(sceneId, sceneData);
  const { deleteScene, isDeleting } = useDeleteScene(sceneId);

  const characters = useMemo(() =>
    Object.values(novel.summaries.characters),
    [novel]
  );
  const locations = useMemo(() =>
    Object.values(novel.summaries.locations),
    [novel]
  );
  const organizations = useMemo(() =>
    Object.values(novel.summaries.organizations),
    [novel]
  );
  const events = useMemo(() =>
    Object.values(novel.summaries.events).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [novel]
  );

  const form = useForm<SceneFormValues>({
    resolver: zodResolver(SceneFormSchema),
    defaultValues: {
      name: '',
      description: '',
      eventIds: [],
      chapterId: '',
      characterIds: [],
      minorCharacters: '',
      organizationIds: [],
      locationIds: [],
      settings: '',
      pointOfScene: '',
      themes: '',
      changesToMake: '',
      povCharacterId: '',
      status: { phase: 'idle' },
      goal: '',
      conflict: '',
    },
  });

  const { register, control, handleSubmit, reset, watch, formState: { errors } } = form;

  useEffect(() => {
    if (sceneData) {
      reset({
        name: sceneData.name || '',
        description: sceneData.description || '',
        eventIds: sceneData.eventIds || [],
        chapterId: sceneData.chapterId || '',
        characterIds: sceneData.characterIds || [],
        minorCharacters: sceneData.minorCharacters || '',
        organizationIds: sceneData.organizationIds || [],
        locationIds: sceneData.locationIds || [],
        settings: sceneData.settings || '',
        pointOfScene: sceneData.pointOfScene || '',
        themes: sceneData.themes || '',
        changesToMake: sceneData.changesToMake || '',
        povCharacterId: sceneData.povCharacterId || '',
        status: sceneData.status || { phase: 'idle' },
        goal: sceneData.goal || '',
        conflict: sceneData.conflict || '',
      });
    }
  }, [sceneData, reset]);

  useSceneEventAutoFill(form, events, false);

  async function onSubmit(values: SceneFormValues) {
    await updateScene(values);
  }

  async function handleDelete() {
    await deleteScene();
  }

  const selectedEventIds = watch('eventIds') || [];
  const selectedCharacterIds = watch('characterIds') || [];
  const selectedOrganizationIds = watch('organizationIds') || [];

  const selectedCharacterNames = useMemo(() =>
    selectedCharacterIds
      .map((id: string) => characters.find((c) => c.id === id)?.name)
      .filter((name): name is string => !!name),
    [selectedCharacterIds, characters]
  );

  const selectedOrganizationNames = useMemo(() =>
    selectedOrganizationIds
      .map((id: string) => organizations.find((o) => o.id === id)?.name)
      .filter((name): name is string => !!name),
    [selectedOrganizationIds, organizations]
  );

  const selectedEvents = useMemo(() =>
    selectedEventIds
      .map((id: string) => events.find((e) => e.id === id))
      .filter((e): e is EventSummary => !!e),
    [selectedEventIds, events]
  );

  return {
    novel,
    t,
    showDeleteConfirm,
    setShowDeleteConfirm,
    selectedEventForPopup,
    setSelectedEventForPopup,
    sceneData,
    isLoadingScene,
    isSaving,
    isDeleting,
    characters,
    locations,
    organizations,
    events,
    register,
    control,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    selectedCharacterNames,
    selectedOrganizationNames,
    selectedEvents,
    handleDelete,
  };
}
export default useEditSceneForm;
